// FT3D encounter ping presentation.
//
// The ping's sound (SoundForge `map_ping__generic`, owner-approved 2026-10-08) is a presentation cue with no roll
// row, so no resolver branch can reach it: my_campaign.php deliverFt3dEncounterPing() — the one place both the
// sender's POST response and the websocket echo arrive, already deduped by ping_id — calls ftPlayEncounterPingCue.
// The take comes from the catalog's 'map_ping/generic' pool, so approving a different take in sfx_review.php
// re-voices the ping with no file copy, and it is seeded by the ping id so the whole table hears the same one.

import type { WSMessage } from './types';
import { loadAudioCatalog, pickAudioTake, seededRandom } from '../shared/audio-catalog';
import { playCampaignCue } from './roll-audio-mixer';

type PingMessage = Extract<WSMessage, { type: 'ft3d_ping' }>;

const PING_POOL = 'map_ping/generic';

function handleFt3dPing(data: PingMessage): void {
    if (!checkIfValidUUID(data.encounter_id) || data.space !== 'ft3d') return;
    const x = Number(data.x);
    const y = Number(data.y);
    const z = Number(data.z);
    if (data.schema !== 'ft.ft3d.encounter-ping.v1'
        || !checkIfValidUUID(String(data.ping_id || ''))
        || ![x, y, z].every(Number.isFinite)) return;
    window.dispatchEvent(new CustomEvent('ft:ft3d-encounter-ping', { detail: data }));
}

/** Play the ping cue once. The caller owns the per-ping dedupe; a missing catalog or pool is silence, never an error. */
async function playEncounterPingCue(pingId: string): Promise<void> {
    try {
        const catalog = await loadAudioCatalog();
        if (!catalog) return;
        const take = pickAudioTake(catalog, [[PING_POOL]], seededRandom(`${String(pingId).toUpperCase()}|ping`));
        if (take) playCampaignCue(take.url);
    } catch {
        // a missing ping sound never blocks the ping
    }
}

window.handleFt3dPing = handleFt3dPing;
(window as unknown as { ftPlayEncounterPingCue?: typeof playEncounterPingCue }).ftPlayEncounterPingCue = playEncounterPingCue;

export { handleFt3dPing, playEncounterPingCue };
